/**
 * MapLibre base map styles — all keyless.
 *
 *  - map:       OpenFreeMap vector styles (fiord = dark navy, positron = light)
 *               © OpenFreeMap © OpenMapTiles Data from OpenStreetMap
 *  - satellite: Esri World Imagery raster
 *  - topo:      OpenTopoMap raster
 *  - plan:      plain dark background (no tiles)
 */

// Glyph (font) endpoint for styles that do not bring their own (raster/plan).
// OpenFreeMap's vector styles use this same endpoint.
export const GLYPHS_URL = 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf';

// Font stacks for the app's own symbol layers. Must be fonts served by GLYPHS_URL.
export const MAP_FONT_STACK = ['Noto Sans Regular'];
export const MAP_FONT_STACK_BOLD = ['Noto Sans Bold'];

export const OPENFREEMAP_ATTRIBUTION = '© OpenFreeMap © OpenMapTiles Data from OpenStreetMap';
export const ESRI_ATTRIBUTION = 'Esri, Maxar, Earthstar Geographics, and the GIS User Community';
export const TOPO_ATTRIBUTION = '© OpenStreetMap contributors, SRTM | © OpenTopoMap (CC-BY-SA)';

// Vector basemap style URLs (attribution comes from the style's own sources)
export const VECTOR_STYLE_URLS = {
  dark: 'https://tiles.openfreemap.org/styles/fiord',
  light: 'https://tiles.openfreemap.org/styles/positron',
};

const ESRI_TILES = 'https://server.arcgisonline.com/ArcGIS/rest/services';

/** Esri raster tile services used as basemaps (the 3D viewer drapes them on the terrain). Note the {z}/{y}/{x} order. */
export const BASEMAP_TILE_SOURCES = {
  satellite: { label: 'Satellite', template: `${ESRI_TILES}/World_Imagery/MapServer/tile/{z}/{y}/{x}`, attribution: ESRI_ATTRIBUTION, maxzoom: 19 },
  streets: { label: 'Street map', template: `${ESRI_TILES}/World_Street_Map/MapServer/tile/{z}/{y}/{x}`, attribution: ESRI_ATTRIBUTION, maxzoom: 19 },
  topo: { label: 'Topographic', template: `${ESRI_TILES}/World_Topo_Map/MapServer/tile/{z}/{y}/{x}`, attribution: ESRI_ATTRIBUTION, maxzoom: 19 },
};

/** Satellite style (Esri World Imagery) */
export const satelliteStyle = {
  version: 8,
  name: 'Satellite',
  glyphs: GLYPHS_URL,
  sources: {
    'satellite-tiles': {
      type: 'raster',
      tiles: [BASEMAP_TILE_SOURCES.satellite.template],
      tileSize: 256,
      maxzoom: 19,
      attribution: ESRI_ATTRIBUTION,
    },
  },
  layers: [
    {
      id: 'satellite-tiles',
      type: 'raster',
      source: 'satellite-tiles',
      paint: { 'raster-opacity': 1 },
    },
  ],
};

/** Topographic style (OpenTopoMap) */
export const topoStyle = {
  version: 8,
  name: 'Topographic',
  glyphs: GLYPHS_URL,
  sources: {
    'topo-tiles': {
      type: 'raster',
      tiles: [
        'https://a.tile.opentopomap.org/{z}/{x}/{y}.png',
        'https://b.tile.opentopomap.org/{z}/{x}/{y}.png',
        'https://c.tile.opentopomap.org/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      maxzoom: 17,
      attribution: TOPO_ATTRIBUTION,
    },
  },
  layers: [
    {
      id: 'background',
      type: 'background',
      paint: { 'background-color': '#0a1020' },
    },
    {
      id: 'topo-tiles',
      type: 'raster',
      source: 'topo-tiles',
      paint: {
        'raster-opacity': 0.85,
        'raster-saturation': -0.3,
        'raster-brightness-max': 0.7,
      },
    },
  ],
};

/** Plain dark style (no basemap tiles — plan view) */
export const planStyle = {
  version: 8,
  name: 'Plan View',
  glyphs: GLYPHS_URL,
  sources: {},
  layers: [
    {
      id: 'background',
      type: 'background',
      paint: { 'background-color': '#0d1117' },
    },
  ],
};

// Old style ids -> current view modes
export const LEGACY_VIEW_MODES = { osm: 'map', dark: 'map' };

/**
 * Resolve the MapLibre `mapStyle` for a view mode and app theme.
 * Returns a style URL (vector 'map') or a style object (raster / plan).
 */
export function getViewModeStyle(viewMode, theme = 'dark') {
  const mode = LEGACY_VIEW_MODES[viewMode] || viewMode;
  switch (mode) {
    case 'satellite': return satelliteStyle;
    case 'topo': return topoStyle;
    case 'plan': return planStyle;
    default: return theme === 'light' ? VECTOR_STYLE_URLS.light : VECTOR_STYLE_URLS.dark;
  }
}

// Non-vector styles, keyed by view mode
export const RASTER_VIEW_MODE_STYLES = {
  satellite: satelliteStyle,
  topo: topoStyle,
  plan: planStyle,
};
